/**
 * LeaderboardScreen — the lobby leaderboard (stats phase).
 *
 * The first consumer of GET /api/leaderboard, whose response shape this phase
 * leaves alone: { category, period, entries[] }. Category and period are the
 * endpoint's own query params, so changing either is one re-request rather
 * than any client-side sorting.
 *
 * Not to be confused with public/rendering/pixi/hud/Leaderboard.js, which is
 * the in-game scoreboard. Same shell-driving pattern as LockerScreen and
 * AccountScreen; fetchFn is injected so this runs in jsdom with no network.
 */

const CATEGORIES = [
  { id: 'kills', label: 'Kills' },
  { id: 'wins', label: 'Wins' },
  { id: 'damage', label: 'Damage' },
  { id: 'kd', label: 'K/D' },
  { id: 'winrate', label: 'Win rate' },
  { id: 'ranked', label: 'Ranked' }
];

const PERIODS = [
  { id: 'daily', label: 'Today' },
  { id: 'weekly', label: 'This week' },
  { id: 'all', label: 'All time' }
];

const COLUMNS = ['#', 'Player', 'Score', 'Kills', 'Deaths', 'Matches'];
const RANKED_COLUMNS = ['#', 'Player', 'Tier', 'RP', 'Wins', 'Matches'];
const PAGE_LIMIT = 100;

const LeaderboardScreen = {
  CATEGORIES,
  PERIODS,
  els: {},
  deps: {},
  category: 'kills',
  period: 'all',
  onKeyDown: null,

  init(deps) {
    this.detachKeys();
    this.category = 'kills';
    this.period = 'all';
    this.deps = { fetchFn: (...a) => fetch(...a), onClose: () => {}, ...deps };
    const byId = (id) => document.getElementById(id);
    this.els = {
      root: byId('leaderboardScreen'), close: byId('leaderboardClose'), status: byId('leaderboardStatus'),
      categories: byId('leaderboardCategories'), periods: byId('leaderboardPeriods'), table: byId('leaderboardTable')
    };
    this.els.close?.addEventListener('click', () => this.hide());
  },

  isVisible() {
    return !!this.els.root && !this.els.root.classList.contains('hidden');
  },

  async show() {
    if (!this.els.root || this.isVisible()) return;
    this.els.root.classList.remove('hidden');
    this.onKeyDown = (e) => { if (e.key === 'Escape') this.hide(); };
    document.addEventListener('keydown', this.onKeyDown);
    this.renderControls();
    await this.load();
  },

  hide() {
    if (!this.els.root) return;
    const wasVisible = this.isVisible();
    this.els.root.classList.add('hidden');
    this.detachKeys();
    if (wasVisible) this.deps.onClose();
  },

  /** Exactly one Escape listener can ever be attached: show() adds, hide()/init() take it away. */
  detachKeys() {
    if (this.onKeyDown) document.removeEventListener('keydown', this.onKeyDown);
    this.onKeyDown = null;
  },

  renderControls() {
    this.renderChips(this.els.categories, CATEGORIES, 'category', this.category, (id) => {
      this.category = id;
      this.renderControls();
      this.load();
    });
    if (this.els.periods) {
      this.els.periods.style.display = this.category === 'ranked' ? 'none' : '';
    }
    if (this.category !== 'ranked') {
      this.renderChips(this.els.periods, PERIODS, 'period', this.period, (id) => {
        this.period = id;
        this.renderControls();
        this.load();
      });
    } else if (this.els.periods) {
      this.els.periods.innerHTML = '';
    }
  },

  renderChips(host, options, datasetKey, activeId, onPick) {
    if (!host) return;
    host.innerHTML = '';
    for (const option of options) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'chip' + (option.id === activeId ? ' active' : '');
      b.dataset[datasetKey] = option.id;
      b.setAttribute('aria-pressed', String(option.id === activeId));
      b.textContent = option.label;
      b.addEventListener('click', () => onPick(option.id));
      host.appendChild(b);
    }
  },

  async load() {
    const url = this.category === 'ranked'
      ? `/api/ranked/leaderboard?season=current&limit=${PAGE_LIMIT}&offset=0`
      : `/api/leaderboard?category=${encodeURIComponent(this.category)}&period=${encodeURIComponent(this.period)}&limit=${PAGE_LIMIT}&offset=0`;
    try {
      const res = await this.deps.fetchFn(url);
      if (!res || !res.ok) throw new Error(`GET ${url} → ${res?.status}`);
      const body = await res.json();
      this.clearStatus();
      this.renderTable(body?.entries);
    } catch {
      this.renderStatus();
    }
  },

  clearStatus() {
    const { status } = this.els;
    if (!status) return;
    status.classList.add('hidden');
    status.innerHTML = '';
  },

  renderStatus() {
    const { status } = this.els;
    if (!status) return;
    status.classList.remove('hidden');
    status.innerHTML = '';
    const msg = document.createElement('span');
    msg.textContent = "Couldn't load the leaderboard.";
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => { this.load(); });
    status.append(msg, retry);
  },

  renderTable(entries) {
    const { table } = this.els;
    if (!table) return;
    table.innerHTML = '';
    const rows = Array.isArray(entries) ? entries : [];
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'stat-empty';
      empty.textContent = 'No entries for this period yet.';
      table.appendChild(empty);
      return;
    }

    const columns = this.category === 'ranked' ? RANKED_COLUMNS : COLUMNS;
    const el = document.createElement('table');
    el.className = 'stat-table leaderboard-table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const label of columns) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = label;
      headRow.appendChild(th);
    }
    thead.appendChild(headRow);

    const tbody = document.createElement('tbody');
    for (const entry of rows) {
      const tr = document.createElement('tr');
      tr.dataset.userId = entry.userId || '';
      const cells = this.category === 'ranked'
        ? [entry.rank, entry.displayName || 'Unknown', entry.tier, entry.rp, entry.wins, entry.matchesPlayed]
        : [entry.rank, entry.displayName || 'Unknown', entry.value, entry.kills, entry.deaths, entry.matchesPlayed];
      for (const value of cells) {
        const td = document.createElement('td');
        td.textContent = String(value ?? 0);   // server copy, rendered as text
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    el.append(thead, tbody);
    table.appendChild(el);
  }
};

export { LeaderboardScreen, CATEGORIES, PERIODS };
